/**
 * A game against the computer for a guest, kept by the browser alone: no account, no server.
 * The browser deals the chance as the server would (R-RNG-03) and plays the computer's side
 * (@brig/bot) at once whenever it is to move, so the screen never waits for it. Only the small
 * map; the game is kept on the device until the next one begins.
 */
import { botTurn } from '@brig/bot';
import { getNation } from '@brig/nations';
import {
  applyDealt,
  sizedSetup,
  type GameSetup,
  type GameSizeId,
  type GameState,
  type GameStep,
  type Side,
} from '@brig/rules-engine';
import type { CreateGameBody } from '@brig/api-types';
import { fromCallback } from 'xstate';
import {
  current,
  DEFAULT_COLOURS,
  DEFAULT_NATIONS,
  historyOf,
  historyReducer,
  randomSeed,
  type Colours,
  type History,
  type HistoryError,
  type Nations,
} from './history.ts';
import type { GameSource } from './screen/useGameScreen.ts';
import type { Command, CommitInput, Committed } from './turnMachine.ts';

/** The only size a guest may play. */
export const GUEST_SIZES: readonly GameSizeId[] = ['small'];

/** The computer's side; the guest plays red, as an account does against the computer. */
const BOT_SIDE: Side = 'blue';

const STORE_KEY = 'brig.guestGame.v1';

/** What the device keeps of a guest's game: enough to replay it. */
export interface GuestGame {
  readonly setup: GameSetup;
  readonly steps: readonly GameStep[];
  readonly nations: Nations;
  readonly colours: Colours;
}

/** A fresh PRNG state from real entropy (R-RNG-03). */
function dealRng(): GameStep['rng'] & {} {
  const [a = 0, b = 0, c = 0, d = 0] = crypto.getRandomValues(new Uint32Array(4));
  // xoshiro must not start from the all-zero state.
  return a === 0 && b === 0 && c === 0 && d === 0 ? [1, 0, 0, 0] : [a, b, c, d];
}

const play = (state: GameState, action: Parameters<typeof applyDealt>[1]) =>
  applyDealt(state, action, dealRng());

const plain = (message: string): HistoryError => ({ rule: null, reason: null, message });

/** A new game on the small map; the computer makes its first turn at once if it has the lot. */
export function newGuestGame(
  nations: Nations = DEFAULT_NATIONS,
  colours: Colours = DEFAULT_COLOURS,
): GuestGame {
  const [size = 'small'] = GUEST_SIZES;
  const setup = sizedSetup(size, randomSeed(), {
    red: { id: 'guest', name: getNation(nations.red).names.en },
    blue: { id: 'bot', name: getNation(nations.blue).names.en },
  });
  const start = current(historyOf(setup, []));
  return { setup, steps: botTurn(start, BOT_SIDE, play).steps, nations, colours };
}

/** The game this device was playing, if it is still on. */
export function loadGuestGame(): GuestGame | null {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null') as GuestGame | null;
    if (!saved) return null;
    const history = historyOf(saved.setup, saved.steps, saved.nations, saved.colours);
    return current(history).status.kind === 'playing' ? saved : null;
  } catch {
    // Unreadable, or from an older engine: a new game it is.
    return null;
  }
}

function saveGuestGame(game: GuestGame): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(game));
  } catch {
    // Without storage the game lasts while the page is open.
  }
}

/**
 * A command against the browser's engine, the computer answering at once: the history after
 * it, and the steps it took with their chance (none for an undo, a recolour or a refusal).
 */
export function guestCommit(
  history: History,
  command: Command,
): { readonly history: History; readonly steps: readonly GameStep[] } {
  switch (command.type) {
    case 'act': {
      let state = current(history);
      const steps: GameStep[] = [];
      for (const action of command.actions) {
        if (action.side === BOT_SIDE) return refused(history, 'You play the other side');
        const { result, step } = play(state, action);
        if (!result.ok) return { history: { ...history, error: result.error }, steps: [] };
        state = result.state;
        steps.push(step);
      }
      try {
        steps.push(...botTurn(state, BOT_SIDE, play).steps);
      } catch (e) {
        return refused(history, e instanceof Error ? e.message : String(e));
      }
      const next = historyReducer(history, { type: 'commit', steps });
      return { history: next, steps: next.error ? [] : steps };
    }
    case 'undo':
      // The computer's moves stand: only the guest's own come back.
      if (history.actions.at(-1)?.side === BOT_SIDE) return { history, steps: [] };
      return { history: historyReducer(history, { type: 'undo' }), steps: [] };
    // A dev tool; a guest has none.
    case 'rewind':
      return { history, steps: [] };
    case 'recolour':
      return { history: historyReducer(history, { type: 'recolour', colours: command.colours }), steps: [] };
  }
}

const refused = (history: History, message: string) => ({
  history: { ...history, error: plain(message) },
  steps: [],
});

/**
 * The screen's source for a guest's game. `onNewGame` starts another (on the small map whatever
 * the body asks) and the screen opens afresh for it.
 */
export function guestSource(game: GuestGame, onNewGame: (game: GuestGame) => void): GameSource {
  // The steps behind the screen's history, with their chance, to keep the game on the device.
  let steps = [...game.steps];
  function commit(input: CommitInput): History {
    const answer = guestCommit(input.history, input.command);
    steps = [...steps.slice(0, input.history.actions.length), ...answer.steps].slice(
      0,
      answer.history.actions.length,
    );
    saveGuestGame({ setup: game.setup, steps, nations: game.nations, colours: answer.history.colours });
    return answer.history;
  }

  saveGuestGame(game);
  return {
    initial: () => historyOf(game.setup, game.steps, game.nations, game.colours),
    commit: fromCallback<{ type: 'NONE' }, CommitInput>(({ input, sendBack }) => {
      sendBack({ type: 'COMMITTED', history: commit(input) } satisfies Committed);
    }),
    devTools: false,
    remote: BOT_SIDE,
    sizes: GUEST_SIZES,
    exitTo: '/',
    newGame: (body: CreateGameBody) => {
      onNewGame(newGuestGame(body.nations, body.colours));
      return Promise.resolve();
    },
  };
}
